import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SidebarComponent } from './layout/containers';
import { HeaderBarComponent } from '@custom-components/layout/header/header-bar/header-bar.component';
import { AboutDialogComponent } from '@custom-components/overlay/about-dialog/about-dialog.component';
import { AuthStore } from '../store/auth.store';
import { APP_PATHS, LANGUAGE_CODE, LocalhostKeys } from '@core/constants';
import { UserStore } from '../store/user.store';
import { Partner } from '@core/models';
import { environment } from '@root/environments/environment';

@Component({
  selector: 'xemex-pages',
  imports: [RouterOutlet, HeaderBarComponent, SidebarComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './pages.component.html',
})
export class PagesComponent {
  readonly userStore = inject(UserStore);
  readonly authStore = inject(AuthStore);
  private readonly dialog = inject(MatDialog);

  readonly DOCS_URL = 'https://docs.xemex-cloud.eu';

  userName = '';
  userAvatar: any;
  partners: Partner[] | undefined = [];
  isLoading = false;
  searchValue = '';

  collapsedMenu = false;

  constructor(
    readonly router: Router,
    readonly translateService: TranslateService,
    route: ActivatedRoute,
  ) {
    this.searchValue = route.snapshot.queryParamMap.get('text') ?? '';

    const userOid = this.authStore.oid();
    this.userStore.getUserMethod(userOid);
    this.userStore.getUserAvatarMethod(userOid);

    effect(() => {
      this.userAvatar = this.userStore.userAvatar();
      this.isLoading = this.userStore.isLoadingAvatar();
      this.partners = this.userStore.user()?.partners;

      const userProfile = this.userStore.user();
      if (userProfile?.firstName || userProfile?.lastName) {
        this.userName = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ');
      }
      if (userProfile?.language) {
        const langCode = LANGUAGE_CODE[userProfile?.language];
        this.translateService.use(langCode);
        // Persist so the login page reached after a token expiry can render in
        // this language before any authenticated profile is available again.
        localStorage.setItem(LocalhostKeys.LANGUAGE, langCode);
      }
    });
  }

  onMenuIconClicked() {
    this.collapsedMenu = !this.collapsedMenu;
  }

  onLogoutClicked() {
    this.authStore.logout();
  }

  onSearch(text: string) {
    this.searchValue = text;
    this.router.navigate(['/', APP_PATHS.Search], {
      queryParams: {
        text: text,
      },
    });
  }

  onPartnerClicked(partner: Partner) {
    this.userStore.switchUserMethod({
      oid: this.userStore.user().object_id,
      partnerOid: partner.partnerOid,
    });
  }

  onProfileClicked() {
    this.router.navigate(['/', APP_PATHS.Profile]);
  }

  onAboutClicked() {
    const { text, urls } = environment.branding;
    this.dialog.open(AboutDialogComponent, {
      width: '32rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100vh - 2rem)',
      data: {
        appName: text.company.name,
        aboutText: text.company.about,
        privacyPolicyUrl: urls.privacyPolicy,
        termsUrl: urls.termsOfService,
        websiteUrl: text.company.website,
      },
    });
  }
}
